/** /zh/comparisons/linear-docs — Chinese version. */
import { MarketingShell } from '../MarketingShell';

export function LinearDocsZh() {
  return (
    <MarketingShell
      currentLocale="zh"
      hreflangPath="/comparisons/linear-docs"
      documentTitle="面向思考阶段的 Linear 替代方案：Draftlize vs Linear Docs · Draftlize"
      documentDescription="Linear Docs 适合事情已成为 issue 之后。Draftlize 是面向之前那段混乱思考阶段的 Linear 替代品——AI agent 帮你打磨不漂移的产品决策。给 PM 的对比。"
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">对比 · vs Linear Docs</span>
          <h1 className="serif" style={{ fontSize: 'clamp(40px, 5vw, 64px)', lineHeight: 1, margin: '12px 0 24px' }}>
            Draftlize vs Linear Docs：<br/>
            <em>思考阶段 vs 出货阶段。</em>
          </h1>
          <p className="lede">
            Linear 是存在的最好的 issue 跟踪 / 项目工具。Linear Docs 把这层延伸到了发版相邻文档。但某件事成为 issue *之前*的思考——那些杂乱的酝酿、被否决的方案、背后的理由——住在别处。Draftlize 是给那个阶段的 substrate。
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">TL;DR</span>
            <h2 className="serif">不同阶段，<em>不同形状。</em></h2>
          </div>
          <p>两个都能跑。多数用 Draftlize 的 PM 同时也用 Linear——Draftlize 管上游思考，Linear 管下游跟踪。</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th></th>
              <th className="ours">Draftlize</th>
              <th>Linear Docs</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>Issue + 项目 + cycle 跟踪</td><td className="no">—</td><td className="yes">出色</td></tr>
            <tr><td>实时团队协作</td><td className="no">单人优先</td><td className="yes">原生</td></tr>
            <tr><td>AI agent 把决策作为 context 读</td><td className="ours yes">原生</td><td className="partial">Linear AI 摘要</td></tr>
            <tr><td>AI agent 能以结构化形式写回</td><td className="ours yes">原生</td><td className="no">—</td></tr>
            <tr><td>决策变化时 stale 传播</td><td className="ours yes">原生</td><td className="no">—</td></tr>
            <tr><td>卡片级历史 + 来源</td><td className="ours yes">每次编辑、作者、来源</td><td className="yes">文档历史</td></tr>
            <tr><td>Claude Code / Cursor / MCP 集成</td><td className="ours yes">原生</td><td className="no">—</td></tr>
            <tr><td>Spec 状态漂移检测（built → drift_detected）</td><td className="ours yes">原生</td><td className="no">—</td></tr>
          </tbody>
        </table>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">Linear <em>赢的地方。</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">已发版工作跟踪</h3>
            <p>Issues、cycle、项目、里程碑——Linear 是存在的最好的执行层。Draftlize 不试图跟 issue；它跟的是*变成* issue 之前的思考。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">团队工作流</h3>
            <p>Linear 是为 5-100 人产品团队建的。实时、快、跟 GitHub / Slack / Figma 深度集成。Draftlize 是单人优先；多用户在 roadmap 上但 v1 没有。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">跟工程工具的集成</h3>
            <p>PR 链接、部署、sentry 集成——Linear 把工程那侧 cover 了。Draftlize 有 Claude Code / Cursor / MCP，服务 AI 辅助 spec→build 流，但不传统 dev 工作流。</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">Draftlize <em>赢的地方。</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">issue 存在之前的思考</h3>
            <p>Linear 假设你已经决定要建这个东西。Draftlize 是给上游那阶段——"该不该建" / "做什么" / "依赖什么"那些思考，最后产生给 Linear 的 issue。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">被否决的方案仍可见</h3>
            <p>Linear 里，被否决的想法消失了。Draftlize 里，它们成 <code>dead_end</code> 类的卡片——agent 看到"这条路探过被否了"而不是 3 个 sprint 后再建议一遍。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">AI agent 原地更新 spec</h3>
            <p>Linear 目前没有能基于对话编辑 spec 的 AI。Draftlize 有——你实现一个 spec（标 <code>built</code>）之后再编辑，状态自动翻 <code>drift_detected</code>，所以你知道哪些跟代码脱钩了。</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          Linear：我们在建什么。Draftlize：为什么决定建它、那个决策动了之后什么会变。
        </blockquote>
        <cite>干净的并用方式：Draftlize 卡片足够具体时升级成 Linear issue。</cite>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">在 Linear <em>上游</em>加 Draftlize。</h2>
        </div>
        <div className="trust-strip">
          <span>注册送 $5</span>
          <span>按量付费</span>
          <span>余额永不过期</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            注册送 $5，按量付费，余额永不过期。Linear 继续管出货。Draftlize 管决定出货什么的思考。
          </p>
          <a className="btn-primary" href="/">用 $5 免费开始</a>
        </div>
      </section>
    </MarketingShell>
  );
}
